import React, { useState, useEffect, useMemo } from 'react';
import { MapPin } from 'lucide-react';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';

// onSelect: (({ location, lat, lng }) => void) — when provided, use it instead of the
// formData/setFormData pair, so this can be reused outside the MethaneForm create flow
// (e.g. Incident.jsx's location picker modal).
export default function ExactLocation({ formData, setFormData, onSelect, label = 'E - Exact Location', placeholder = 'Search a location...' }) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  const autocompleteService = useMemo(() => {
    if (window?.google?.maps?.places) {
      return new window.google.maps.places.AutocompleteService();
    }
    return null;
  }, []);

  const placesService = useMemo(() => {
    if (window?.google?.maps?.places) {
      return new window.google.maps.places.PlacesService(document.createElement('div'));
    }
    return null;
  }, []);

  const fetchPredictions = useMemo(
    () =>
      debounce((input, callback) => {
        if (autocompleteService) {
          autocompleteService.getPlacePredictions({ input }, callback);
        }
      }, 400),
    [autocompleteService]
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService || inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    fetchPredictions(inputValue, (results) => {
      if (active) {
        let newOptions = value ? [value] : [];
        if (results) newOptions = [...newOptions, ...results];
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, value, fetchPredictions, autocompleteService]);

  const fetchPlaceDetails = (placeId) => {
    if (!placesService) return;
    placesService.getDetails({ placeId }, (place) => {
      const location = place.formatted_address;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      if (onSelect) {
        onSelect({ location, lat, lng });
      } else {
        setFormData({
          ...formData,
          location,
          lat,
          lng
        });
      }
    });
  };

  const handleSelect = (option) => {
    setOptions(option ? [option, ...options] : []);
    setValue(option);
    setInputValue(option ? option.description || '' : '');
    setOpen(false);

    if (option?.place_id) {
      fetchPlaceDetails(option.place_id);
    }
  };

  return (
    <div className="relative">
      <label className="form-label">{label}</label>
      <input
        className="form-input"
        value={inputValue}
        placeholder={placeholder}
        onChange={(event) => {
          setInputValue(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && options.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-72 overflow-y-auto dark:bg-ink-900 dark:border-neutral-700">
          {options.map((option, index) => {
            const matches = option.structured_formatting?.main_text_matched_substrings || [];
            const parts = parse(
              option.structured_formatting?.main_text || option.description || '',
              matches.map((match) => [match.offset, match.offset + match.length])
            );

            return (
              <button
                type="button"
                key={option.place_id || index}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
                className="w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 hover:bg-neutral-50 border-b border-neutral-50 last:border-0 dark:hover:bg-ink-800 dark:border-neutral-800"
              >
                <MapPin className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0 dark:text-neutral-500" />
                <div className="min-w-0">
                  <div className="text-sm text-neutral-800 truncate dark:text-neutral-100">
                    {parts.map((part, partIndex) => (
                      <span key={partIndex} className={part.highlight ? 'font-bold' : ''}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                  {option.structured_formatting?.secondary_text && (
                    <div className="text-xs text-neutral-500 truncate dark:text-neutral-500">
                      {option.structured_formatting.secondary_text}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
